import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { DashboardShell } from "@/components/DashboardShell";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertTriangle, CreditCard, Eye, FileText, LayoutDashboard, Loader2, RefreshCw, Users } from "lucide-react";
import { toast } from "sonner";

const NAV = [
  { key: "home", label: "Overview", to: "/admin", icon: LayoutDashboard },
  { key: "users", label: "Users & Papers", to: "/admin/users", icon: Users },
  { key: "plans", label: "Purchase Plans", to: "/admin/plans", icon: CreditCard },
  { key: "papers", label: "Question Papers", to: "/admin/papers", icon: FileText },
  { key: "failed", label: "Failed evaluations", to: "/admin/failed-evaluations", icon: AlertTriangle },
];

export default function FailedEvaluations() {
  const [scripts, setScripts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/answer-scripts");
      setScripts(data.filter((script) => script.status === "failed"));
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Could not load failed evaluations");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <DashboardShell nav={NAV} title="Failed evaluations">
      <div className="bg-background border border-border rounded-lg p-6" data-testid="admin-failed-evaluations">
        <div className="flex items-start justify-between gap-4 mb-5">
          <div>
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              <h2 className="font-heading font-bold text-lg">Failed evaluations ({scripts.length})</h2>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">Inspect a failed answer script and retry its evaluation.</p>
          </div>
          <Button variant="outline" size="sm" className="rounded-full gap-2" onClick={load} disabled={loading}>
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : scripts.length === 0 ? (
          <p className="py-12 text-center text-sm text-muted-foreground">No failed evaluations.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Student</TableHead>
                <TableHead>Paper</TableHead>
                <TableHead>Submitted</TableHead>
                <TableHead>Failure</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {scripts.map((script) => (
                <TableRow key={script.id} data-testid={`admin-failed-script-${script.id}`}>
                  <TableCell>
                    <p className="font-medium">{script.student_name || "Unknown student"}</p>
                    <p className="text-xs text-muted-foreground">{script.student_email || "—"}</p>
                  </TableCell>
                  <TableCell>
                    <p className="font-medium">{script.paper_title}</p>
                    <p className="text-xs text-muted-foreground">{script.subject}</p>
                  </TableCell>
                  <TableCell>{script.created_at ? new Date(script.created_at).toLocaleString() : "—"}</TableCell>
                  <TableCell className="max-w-xs text-destructive">{script.error || "Evaluation failed. Please retry."}</TableCell>
                  <TableCell className="text-right">
                    <Button asChild size="sm" variant="outline" className="rounded-full gap-1.5">
                      <Link to={`/result/${script.id}`}><Eye className="h-3.5 w-3.5" /> Inspect</Link>
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </DashboardShell>
  );
}
